export type ImageOutputFormat = 'jpeg' | 'png' | 'webp'

export interface CompressionResult {
  blob: Blob
}

const MIME_BY_FORMAT: Record<ImageOutputFormat, string> = {
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
}

const EXTENSION_BY_FORMAT: Record<ImageOutputFormat, string> = {
  jpeg: 'jpg',
  png: 'png',
  webp: 'webp',
}

/**
 * Compress an image locally through the Canvas pipeline:
 * decode (createImageBitmap) → redraw → encode (toBlob).
 *
 * Codec abstraction: currently the browser's native encoders power this path.
 * The interface is kept codec-agnostic so a WASM codec (e.g. Squoosh's webp)
 * can be swapped in later without touching callers.
 */
export async function compressImage(
  file: File,
  format: ImageOutputFormat,
  quality: number,
  onProgress: (progress: number) => void,
): Promise<CompressionResult> {
  onProgress(15)

  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    throw new Error('Could not decode this image. The file may be corrupted or not a valid image.')
  }

  onProgress(45)

  const canvas = document.createElement('canvas')
  canvas.width = bitmap.width
  canvas.height = bitmap.height

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    bitmap.close()
    throw new Error('Canvas 2D is not supported in this browser.')
  }

  // JPEG has no alpha channel: fill white first, otherwise transparency
  // would render as black.
  if (format === 'jpeg') {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }

  ctx.drawImage(bitmap, 0, 0)
  bitmap.close()

  onProgress(70)

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, MIME_BY_FORMAT[format], quality / 100)
  })

  if (!blob) {
    throw new Error(
      `This browser does not support ${format.toUpperCase()} encoding. Try a different output format.`,
    )
  }

  onProgress(100)
  return { blob }
}

/** Output file name preserving the original stem, e.g. photo.png -> photo.webp. */
export function outputFileName(originalName: string, format: ImageOutputFormat): string {
  const stem = originalName.replace(/\.[^.]+$/, '')
  return `${stem}.${EXTENSION_BY_FORMAT[format]}`
}
